import { engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { BaseController } from "./BaseController";
import { Paylines } from "../Paylines";
import { PaylinesSelector } from "../PaylinesSelector";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { WinAnimator2 } from "../../CustomComponents/WinAnimator2";
import { GameManager } from "../../Managers/GameManager";
import { AppContext } from "../../../Common/AppContext";
import { FrameTimerEventEmitter, CreateFrameTimer, FrameTimerEvents } from "../../Utils/FrameTimer";
import { PlayList } from "../../Components/Sounds/PlayList";
import { DataManager } from "../../Managers/DataManager";
import { Tweens } from "../../Components/Effects/Tweens";
import { ImageElement } from "../../Components/Elements/Graphics/ImageElement";

type IWin = Communication.Game.IWin;

@engineComponent(ComponentTypes.DisplayObject)
export class BaseView extends ContainerElement {
    private _controller: BaseController;
    private _wasFinished: boolean = false;
    private _outroDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _paylinesSelector: PaylinesSelector;

    private _symbols: Symbols;
    private _machine: Machine;
    private _noWinContainer: ContainerElement;
    private _winAnimator: WinAnimator2;

    private _paylinesBackground: ContainerElement;
    private _reels: SwitchElement;
    private _background: SwitchElement;
    private _logo: ImageElement;

    private _lastHighestWin: number;

    constructor() {
        super();

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));
    }

    create(node: Element): void {
        super.create(node);

        if (this.childs != null) {
            for (var key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                }

                if (this.childs[key] instanceof PaylinesSelector) {
                    this._paylinesSelector = this.childs[key];
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof Machine) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimator) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                }

                if (key === "Reels" && this.childs[key] instanceof SwitchElement) {
                    this._reels = this.childs[key];
                }

                if (key === "PaylinesBackground" && this.childs[key] instanceof ContainerElement) {
                    this._paylinesBackground = this.childs[key];
                }

                if (key === "Background" && this.childs[key] instanceof SwitchElement) {
                    this._background = this.childs[key];
                }

                if (key === "Logo" && this.childs[key] instanceof ImageElement) {
                    this._logo = this.childs[key];
                }
            }

            if (this._symbols == null) {
                if (DEBUG) {
                    this.log("ERROR no symbols");
                }
            }

            if (this._machine == null) {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }

            if (this._paylines == null) {
                if (DEBUG) {
                    this.log("WARNING no paylines");
                }
            }

            if (this._winAnimator == null) {
                if (DEBUG) {
                    this.log("WARNING no winAnimator");
                }
            }
        }
    }

    initReceived(controller: BaseController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.baseResponse != null) {
                    if (GameManager.baseResponse.initData != null && GameManager.baseResponse.initData.reels != null) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(this._symbols, GameManager.baseResponse.initData.reels);
                    }

                    if (
                        GameManager.baseResponse.screen != null &&
                        GameManager.baseResponse.screen.reels != null &&
                        GameManager.baseResponse.screen.reels.length !== 0
                    ) {
                        if (this._winAnimator != null) {
                            this._winAnimator.on(
                                WinAnimator.FinishWinCalculation,
                                this.finishWinCalculation.bind(this),
                            );
                            this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                            this._winAnimator.on(
                                WinAnimator.FinishQuickWinCalculation,
                                this.finishQuickWinCalculation.bind(this),
                            );
                            this._winAnimator.initialize(this._symbols, this._paylines, this._noWinContainer);
                            this._winAnimator.showSymbols(GameManager.baseResponse.screen.reels);
                        }
                        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reels in response");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no base reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        this.toggleElements(false);
    }

    disableLogo(): void {
        if (this._logo != null) {
            this.removeChild(this._logo);
            this._logo = null;
        }
    }

    toggleElements(visible: boolean, long: boolean = false): void {
        if (long) {
            if (visible) {
                if (this._reels != null) {
                    this._reels.visible = true;
                    Tweens.removeTweens(this._reels, "all");
                    Tweens.alphaTween(this._reels, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                }
                if (this._noWinContainer != null) {
                    this._noWinContainer.visible = true;
                    Tweens.removeTweens(this._noWinContainer, "all");
                    Tweens.alphaTween(this._noWinContainer, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                }
                if (this._paylines != null) {
                    this._paylines.visible = true;
                    Tweens.removeTweens(this._paylines, "all");
                    Tweens.alphaTween(this._paylines, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                }
                if (this._winAnimator != null) {
                    this._winAnimator.visible = true;
                    Tweens.removeTweens(this._winAnimator, "all");
                    Tweens.alphaTween(this._winAnimator, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                }
                if (this._paylinesBackground != null) {
                    this._paylinesBackground.visible = true;
                    Tweens.removeTweens(this._paylinesBackground, "all");
                    Tweens.alphaTween(this._paylinesBackground, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                }
                if (this._logo != null) {
                    this._logo.visible = true;
                    Tweens.removeTweens(this._logo, "all");
                    Tweens.alphaTween(this._logo, {
                        alphas: [0, 1],
                        tweenDuration: 15,
                    });
                }
            } else {
                if (this._reels != null) {
                    Tweens.removeTweens(this._reels, "all");
                    Tweens.alphaTween(this._reels, {
                        alphas: [1, 0],
                        tweenDuration: 15,
                    });
                }
                if (this._noWinContainer != null) {
                    Tweens.removeTweens(this._noWinContainer, "all");
                    Tweens.alphaTween(this._noWinContainer, {
                        alphas: [1, 0],
                        tweenDuration: 15,
                    });
                }
                if (this._paylines != null) {
                    Tweens.removeTweens(this._paylines, "all");
                    Tweens.alphaTween(this._paylines, {
                        alphas: [1, 0],
                        tweenDuration: 15,
                    });
                }
                if (this._winAnimator != null) {
                    Tweens.removeTweens(this._winAnimator, "all");
                    Tweens.alphaTween(this._winAnimator, {
                        alphas: [1, 0],
                        tweenDuration: 15,
                    });
                }
                if (this._paylinesBackground != null) {
                    Tweens.removeTweens(this._paylinesBackground, "all");
                    Tweens.alphaTween(this._paylinesBackground, {
                        alphas: [1, 0],
                        tweenDuration: 15,
                    });
                }
                if (this._logo != null) {
                    Tweens.removeTweens(this._logo, "all");
                    Tweens.alphaTween(this._logo, {
                        alphas: [1, 0],
                        tweenDuration: 15,
                    });
                }
            }
        } else {
            if (this._reels != null) {
                this._reels.visible = visible;
            }
            if (this._noWinContainer != null) {
                this._noWinContainer.visible = visible;
            }
            if (this._paylines != null) {
                this._paylines.visible = visible;
            }
            if (this._winAnimator != null) {
                this._winAnimator.visible = visible;
            }
            if (this._paylinesBackground != null) {
                this._paylinesBackground.visible = visible;
            }
            if (this._logo != null) {
                this._logo.visible = visible;
            }
        }
    }

    initIntro(): void {
        this.highestWin();

        if (GameManager.baseResponse.currentWin > 0) {
            PlayList.loop("SND_BaseRecaploop");
        } else {
            PlayList.loop("SND_BaseLoop");
        }
        this._winAnimator.showInitWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin);
        this.toggleElements(true);
    }

    collect(): void {
        if (GameManager.gameResponse.totalWin > 0) {
            this.highestWin(false);
        }
        PlayList.stop("SND_BaseRecaploop");
        PlayList.loop("SND_BaseLoop");
        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }
        this._winAnimator.reset(true);
    }

    spin(): void {
        this.highestWin(false);
        PlayList.stop("SND_BaseRecaploop");
        PlayList.loop("SND_BaseLoop");
        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }
        this._machine.spinStart(this._winAnimator.reset(false));
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.baseResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.baseResponse.screen.reels, this._machine.actualReelStopped);
    }

    machineStopped(): void {
        this.highestWin();
        this._wasFinished = false;
        this._controller.machineStopped();
        if (this._winAnimator.showWins(GameManager.baseResponse.wins, GameManager.baseResponse.currentWin)) {
            PlayList.stop("SND_BaseLoop");
        }
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    finishWinCalculation(): void {
        this._wasFinished = true;
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        if (!DataManager.Autoplay) {
            PlayList.stop("SND_BaseLoop");
            PlayList.loop("SND_BaseRecaploop");
        }
        this._controller.allWinsFinished();
    }

    resetWinAnimator(): void {
        this._winAnimator.reset(true);
        this.highestWin(false);
    }

    intro(): void {
        PlayList.loop("SND_BaseLoop");
        this.toggleElements(true, true);
    }

    outro(): void {
        this.highestWin(false);
        PlayList.stop("SND_BaseLoop");
        PlayList.stop("SND_BaseRecaploop");
        if (this._paylinesSelector != null) {
            this._paylinesSelector.reset();
        }

        let wasFinished: boolean = this._wasFinished;
        let inHighWins: boolean = this._winAnimator.reset(true);

        if (wasFinished) {
            this.outroFinished();
        } else {
            if (inHighWins) {
                this._outroDelay.start(60);
            } else {
                this._outroDelay.start(30);
            }
        }
    }

    outroFinished(): void {
        this.toggleElements(false, true);
        this._controller.outroFinished();
    }

    selectPayline(): void {
        if (this._paylinesSelector != null) {
            this._paylinesSelector.select();
        }
    }

    private highestWin(win: boolean = true): void {
        if (!win) {
            if (this._reels != null) {
                this._reels.goToCase("NORMAL");
            }
        }

        if (
            GameManager.baseResponse != null &&
            GameManager.baseResponse.wins != null &&
            GameManager.baseResponse.wins.length > 0
        ) {
            if (win) {
                if (this._reels != null) {
                    this._reels.goToCase("WIN");
                }
            }
            let highestWin: IWin = null;

            for (let i: number = 0; i < GameManager.baseResponse.wins.length; i++) {
                if (highestWin == null) {
                    highestWin = GameManager.baseResponse.wins[i];
                } else {
                    let nextWin: IWin = GameManager.baseResponse.wins[i];
                    if (nextWin.amount > highestWin.amount) {
                        highestWin = nextWin;
                    }
                }
            }

            if (highestWin != null && highestWin.payoutId != null) {
                if (this._background != null) {
                    if (win) {
                        this._background.goToCase("WIN");
                        this._lastHighestWin = highestWin.payoutId;
                    } else {
                        if (this._background.getCurrentCase() != "NORMAL") {
                            this._background.goToCase("WIN_END");
                        }
                        this._lastHighestWin = null;
                    }
                }
            }
        } else {
            if (this._background != null) {
                if (this._lastHighestWin != null) {
                    this._background.goToCase("WIN_END");
                    this._lastHighestWin = null;
                }
            }
        }
    }

    disableBase(): void {
        this._winAnimator.resetTimer();
    }

    /**
     * Toggle all sounds used in base game (background, effects, .etc)
     * @param enable
     */
    toggleSounds(enable: boolean): void {
        if (enable) {
            PlayList.loop("SND_BaseLoop");
        } else {
            PlayList.stop("SND_BaseLoop");
            PlayList.stop("SND_BaseRecaploop");
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Base/BaseView.ts
 **/